import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import {
  Home,
  BarChart3,
  Bell,
  User,
  Users,
  LogOutIcon,
  Inbox,
  NotebookTabs,
  CalendarFold,
} from "lucide-react";

// Hardcoded dropdown options — backend accepts any free-text value
const BUILDINGS = ["Main Building", "Others, please specify"];
const MEETING_LOCATIONS = ["West Wing", "East Wing", "Others, please specify"];
const PURPOSES = ["Meeting", "Interview", "Delivery", "Maintenance", "Others, please specify"];
const VISITOR_TYPES = ["VIP/VVIP", "Client/Customer", "Temporary Staff", "Support", "Others, please specify"];

// Auth0 Management API configuration - VMS
const config = {
  domain: "vms-optimus.us.auth0.com",
  clientId: "ZxowKlLpWMVvNbUuzMKooWx0FA5jhxYg",
  audience: "https://vms-optimus.us.auth0.com/api/v2/",
};

// Fetch access token
const getAccessToken = async () => {
  const response = await fetch(`https://${config.domain}/oauth/token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      client_id: config.clientId,
      client_secret:
        "XTH0sFAFWTqpZe8gYn9PJtVU4rg-6hctN7PROhojbsQJ2ZSVy7Nabk5kZSoihlnm",
      audience: config.audience,
      grant_type: "client_credentials",
      scope:
        "read:users read:users_app_metadata update:users_app_metadata read:user_idp_tokens read:client_grants create:users update:users delete:users read:clients read:client_credentials create:client_credentials read:roles update:roles create:roles",
    }),
  });
  const data = await response.json();
  return data.access_token;
};

// Individual Form Component
const IndividualForm = ({
  formData,
  setFormData,
  saveToAddressBook,
  setSaveToAddressBook,
  existingContactMessage,
  setExistingContactMessage,
  errors,
  contacts,
  showSuggestions,
  setShowSuggestions,
  getFilteredContacts,
  handleContactSelect,
}) => {
  const [showOther, setShowOther] = useState({
    building: !BUILDINGS.slice(0,-1).includes(formData.building) && !!formData.building,
    meetingLocation: !MEETING_LOCATIONS.slice(0,-1).includes(formData.meetingLocation) && !!formData.meetingLocation,
    purpose: !PURPOSES.slice(0,-1).includes(formData.purposeOfVisit) && !!formData.purposeOfVisit,
    visitorType: !VISITOR_TYPES.slice(0,-1).includes(formData.visitorType) && !!formData.visitorType,
    documentType: !["passport","permanent_ic",""].includes(formData.documentType) && !!formData.documentType,
  });

  const handleDropdown = (field, value, formKey) => {
    if (value === "Others, please specify") {
      setShowOther(prev => ({ ...prev, [field]: true }));
      setFormData(prev => ({ ...prev, [formKey]: "" }));
    } else {
      setShowOther(prev => ({ ...prev, [field]: false }));
      setFormData(prev => ({ ...prev, [formKey]: value }));
    }
  };

  // Render function (NOT a component) — keeps the input mounted across re-renders so focus never breaks
  const renderOtherableSelect = ({ field, formKey, options, placeholder, value, className = "" }) =>
    showOther[field] ? (
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => setFormData(prev => ({ ...prev, [formKey]: e.target.value }))}
          placeholder={`Specify ${placeholder.toLowerCase()}...`}
          autoFocus
          className={`w-full p-3 border border-blue-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10 ${className}`}
        />
        <button
          type="button"
          onClick={() => { setShowOther(prev => ({ ...prev, [field]: false })); setFormData(prev => ({ ...prev, [formKey]: "" })); }}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-xl leading-none"
          title="Back to list"
        >×</button>
      </div>
    ) : (
      <select
        value={value}
        onChange={(e) => handleDropdown(field, e.target.value, formKey)}
        required
        className={`w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${className}`}
      >
        <option value="">Select {placeholder.toLowerCase()}</option>
        {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    );

  return (
  <div className="max-w-6xl mx-auto w-full pl-12">
    <div className="text-center mb-8">
      <h1 className="text-2xl font-semibold text-gray-900 mb-2">
        Invite Guest
      </h1>
      <p className="text-gray-500">Please fill the guest details to proceed</p>
    </div>

    {/* Row 1: name, email */}

    <div className="grid grid-cols-2 gap-4 mb-6">
      <div className="relative">
        <label className="block text-gray-700 mb-2 font-medium">
          Name
          {errors.name && (
            <span className="text-red-500 text-sm ml-2">{errors.name}</span>
          )}
        </label>
        <input
          type="text"
          placeholder="Enter full name"
          value={formData.name}
          onChange={(e) => {
            setFormData({ ...formData, name: e.target.value });
            const suggestions = getFilteredContacts(e.target.value, "name");
            setShowSuggestions({
              name: suggestions.length > 0 ? suggestions : false,
            });
          }}
          onFocus={(e) => {
            const suggestions = getFilteredContacts(e.target.value, "name");
            setShowSuggestions({
              name: suggestions.length > 0 ? suggestions : false,
            });
          }}
          onBlur={() => {
            setTimeout(() => setShowSuggestions({}), 150);
          }}
          required
          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        {/* Suggestions dropdown */}
        {showSuggestions.name && (
          <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
            {showSuggestions.name.map((contact, index) => (
              <div
                key={index}
                onClick={() => handleContactSelect(contact)}
                className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                <div className="text-sm font-medium text-gray-900">
                  {contact.name}
                </div>
                <div className="text-xs text-gray-500">{contact.email}</div>
                <div className="text-xs text-gray-400">{contact.company}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <label className="block text-gray-700 mb-2 font-medium">
          Email
          {errors.email && (
            <span className="text-red-500 text-sm ml-2">{errors.email}</span>
          )}
        </label>{" "}
        <input
          type="email"
          placeholder="Enter guest email"
          value={formData.email}
          required
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
    </div>

    {/* Row 2: Company Name, Building */}
    <div className="grid grid-cols-3 gap-4 mb-6">
      <div>
        <label className="block text-gray-700 mb-2 font-medium">
          Company Name
          {errors.companyName && (
            <span className="text-red-500 text-sm ml-2">
              {errors.companyName}
            </span>
          )}
        </label>
        <input
          type="text"
          placeholder="Enter company name"
          value={formData.companyName}
          required
          onChange={(e) =>
            setFormData({ ...formData, companyName: e.target.value })
          }
          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <div>
        <label className="block text-gray-700 mb-2 font-medium">
          Building
          {errors.building && (
            <span className="text-red-500 text-sm ml-2">{errors.building}</span>
          )}
        </label>
        {renderOtherableSelect({ field: "building", formKey: "building", options: BUILDINGS, placeholder: "Building", value: formData.building })}
      </div>

      <div>
  <label className="block text-gray-700 mb-2 font-medium">
    Meeting Location
    {errors.meetingLocation && (
      <span className="text-red-500 text-sm ml-2">
        {errors.meetingLocation}
      </span>
    )}
  </label>
  {renderOtherableSelect({ field: "meetingLocation", formKey: "meetingLocation", options: MEETING_LOCATIONS, placeholder: "Meeting Location", value: formData.meetingLocation })}
</div>
    </div>

    {/* Row 3: Date of visit, Start time, End time */}
    <div className="grid grid-cols-4 gap-4 mb-6">
      <div>
        <label className="block text-gray-700 mb-2 font-medium">
          Date of visit
          {errors.dateOfVisit && (
            <span className="text-red-500 text-sm ml-2">
              {errors.dateOfVisit}
            </span>
          )}
        </label>
        <input
          type="date"
          value={formData.dateOfVisit}
          min={new Date().toISOString().split("T")[0]}
          required
          onChange={(e) =>
            setFormData({ ...formData, dateOfVisit: e.target.value })
          }
          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <div>
        <label className="block text-gray-700 mb-2 font-medium">
          Purpose of visit
          {errors.purposeOfVisit && (
            <span className="text-red-500 text-sm ml-2">
              {errors.purposeOfVisit}
            </span>
          )}
        </label>
        {renderOtherableSelect({ field: "purpose", formKey: "purposeOfVisit", options: PURPOSES, placeholder: "Purpose", value: formData.purposeOfVisit })}
      </div>

      <div>
        <label className="block text-gray-700 mb-2 font-medium">
          Start time
          {errors.startTime && (
            <span className="text-red-500 text-sm ml-2">
              {errors.startTime}
            </span>
          )}
        </label>
        <input
          type="time"
          value={formData.startTime}
          required
          onChange={(e) =>
            setFormData({ ...formData, startTime: e.target.value })
          }
          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <div>
        <label className="block text-gray-700 mb-2 font-medium">
          End time
          {errors.endTime && (
            <span className="text-red-500 text-sm ml-2">{errors.endTime}</span>
          )}
        </label>
        <input
          type="time"
          value={formData.endTime}
          onChange={(e) =>
            setFormData({ ...formData, endTime: e.target.value })
          }
          required
          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
    </div>

    {/* Recurring visit - commented out */}

    {/* Type of Visitor */}
    <div className="flex justify-between items-start">
      {/* Type of Visitor - CHANGED TO DROPDOWN */}
<div>
  <label className="block text-gray-700 mb-4 font-medium">
    Type of Visitor
    {errors.visitorType && (
      <span className="text-red-500 text-sm ml-2">
        {errors.visitorType}
      </span>
    )}
  </label>
  {renderOtherableSelect({ field: "visitorType", formKey: "visitorType", options: VISITOR_TYPES, placeholder: "Visitor Type", value: formData.visitorType })}
</div>

      {/* Save to Address Book checkbox - moved to right side */}
      <div className="flex flex-col items-end">
        <div className="flex items-center mt-10">
          <input
            type="checkbox"
            checked={saveToAddressBook}
            onChange={(e) => {
              setSaveToAddressBook(e.target.checked);
              if (!e.target.checked) {
                setExistingContactMessage("");
              }
            }}
            className="mr-3 w-4 h-4 text-blue-800 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
          />
          <label className="text-gray-700 font-medium">
            Save as Frequent Visitor
          </label>
        </div>

        {/* Existing contact message */}
        {existingContactMessage && (
          <div className="mt-2 text-sm text-blue-800">
            {existingContactMessage}
          </div>
        )}
      </div>
    </div>

    {/* Drive-in and Document Checkboxes */}
    <div className="mt-6 space-y-3">
      {/* Drive-in checkbox - inline with input */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="isDriving"
            checked={formData.isDriving}
            onChange={(e) =>
              setFormData({ ...formData, isDriving: e.target.checked, licensePlate: e.target.checked ? formData.licensePlate : "" })
            }
            className="mr-2 w-4 h-4 text-blue-800 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="isDriving" className="text-gray-700 font-medium whitespace-nowrap">Drive-in?</label>
        </div>
        {formData.isDriving && (
          <div className="flex items-center gap-2">
            <label className="text-gray-700 font-medium text-sm whitespace-nowrap">
              License Plate
              {errors.licensePlate && (
                <span className="text-red-500 text-xs ml-1">{errors.licensePlate}</span>
              )}
            </label>
            <input
              type="text"
              placeholder="e.g. TN01AB1234"
              value={formData.licensePlate}
              onChange={(e) => setFormData({ ...formData, licensePlate: e.target.value })}
              className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm w-44"
            />
          </div>
        )}
      </div>

      {/* Document submission required checkbox */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="requiresDocument"
            checked={formData.requiresDocument}
            onChange={(e) =>
              setFormData(prev => ({ ...prev, requiresDocument: e.target.checked, documentType: e.target.checked ? prev.documentType : "" }))
            }
            className="mr-2 w-4 h-4 text-blue-800 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="requiresDocument" className="text-gray-700 font-medium whitespace-nowrap">Document submission required?</label>
        </div>
        {formData.requiresDocument && (
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-gray-700 font-medium text-sm whitespace-nowrap">
              Document Type
              {errors.documentType && (
                <span className="text-red-500 text-xs ml-1">{errors.documentType}</span>
              )}
            </label>
            {showOther.documentType ? (
              <div className="relative">
                <input
                  type="text"
                  value={formData.documentType}
                  onChange={(e) => setFormData(prev => ({ ...prev, documentType: e.target.value }))}
                  placeholder="Specify document type..."
                  autoFocus
                  className="p-2 border border-blue-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm pr-8 w-52"
                />
                <button type="button" onClick={() => { setShowOther(prev => ({ ...prev, documentType: false })); setFormData(prev => ({ ...prev, documentType: "" })); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-xl leading-none">×</button>
              </div>
            ) : (
              <select
                value={formData.documentType}
                onChange={(e) => {
                  if (e.target.value === "others") {
                    setShowOther(prev => ({ ...prev, documentType: true }));
                    setFormData(prev => ({ ...prev, documentType: "" }));
                  } else {
                    setFormData(prev => ({ ...prev, documentType: e.target.value }));
                  }
                }}
                className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">Select type</option>
                <option value="passport">Passport</option>
                <option value="permanent_ic">Permanent Identity Card</option>
                <option value="others">Others, please specify</option>
              </select>
            )}
          </div>
        )}
      </div>

      {/* Approval Required checkbox */}
      <div className="flex items-center">
        <input
          type="checkbox"
          id="isApprovalRequired"
          checked={formData.isApprovalRequired}
          onChange={(e) => setFormData(prev => ({...prev, isApprovalRequired: e.target.checked}))}
          className="mr-2 w-4 h-4 text-blue-800 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
        />
        <label htmlFor="isApprovalRequired" className="text-gray-700 font-medium whitespace-nowrap">Approval Required?</label>
      </div>
    </div>
  </div>
  );
};

const GroupForm = ({
  formData,
  setFormData,
  groupStep,
  numberOfGuests,
  setNumberOfGuests,
  guests,
  setGuests,
  groupErrors = {},
  saveGroupToAddressBook,
  setSaveGroupToAddressBook,
  setGroupContactMessages,
  groupContactMessages,
  contacts,
  showGroupSuggestions,
  setShowGroupSuggestions,
  getFilteredContacts,
  handleGroupContactSelect,
}) => {
  const [showOther, setShowOther] = useState({
    building: !BUILDINGS.slice(0,-1).includes(formData.building) && !!formData.building,
    meetingLocation: !MEETING_LOCATIONS.slice(0,-1).includes(formData.meetingLocation) && !!formData.meetingLocation,
    purpose: !PURPOSES.slice(0,-1).includes(formData.purposeOfVisit) && !!formData.purposeOfVisit,
    visitorType: !VISITOR_TYPES.slice(0,-1).includes(formData.visitorType) && !!formData.visitorType,
    documentType: !["passport","permanent_ic",""].includes(formData.documentType) && !!formData.documentType,
  });

  const handleDropdown = (field, value, formKey) => {
    if (value === "Others, please specify") {
      setShowOther(prev => ({ ...prev, [field]: true }));
      setFormData(prev => ({ ...prev, [formKey]: "" }));
    } else {
      setShowOther(prev => ({ ...prev, [field]: false }));
      setFormData(prev => ({ ...prev, [formKey]: value }));
    }
  };

  // Render function (NOT a component) — keeps input mounted across re-renders so focus never breaks
  const renderOtherableSelect = ({ field, formKey, options, placeholder, value, className = "" }) =>
    showOther[field] ? (
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => setFormData(prev => ({ ...prev, [formKey]: e.target.value }))}
          placeholder={`Specify ${placeholder.toLowerCase()}...`}
          autoFocus
          className={`w-full p-3 border border-blue-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10 ${className}`}
        />
        <button
          type="button"
          onClick={() => { setShowOther(prev => ({ ...prev, [field]: false })); setFormData(prev => ({ ...prev, [formKey]: "" })); }}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-xl leading-none"
          title="Back to list"
        >×</button>
      </div>
    ) : (
      <select
        value={value}
        onChange={(e) => handleDropdown(field, e.target.value, formKey)}
        required
        className={`w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${className}`}
      >
        <option value="">Select {placeholder.toLowerCase()}</option>
        {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
      </select>
    );

  // Initialize guests array when numberOfGuests changes
  useEffect(() => {
    const newGuests = Array.from(
      { length: numberOfGuests },
      (_, index) =>
        guests[index] || { name: "", email: "", company: "" }
    );
    setGuests(newGuests);
  }, [numberOfGuests, setGuests]);

  // Update individual guest data
  const updateGuest = (index, field, value) => {
    const updatedGuests = [...guests];
    updatedGuests[index] = { ...updatedGuests[index], [field]: value };
    setGuests(updatedGuests);
  };

  // Group Step 1: Basic group info
  if (groupStep === 1) {
    return (
      <div className="max-w-7xl  pl-16 w-full">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            Invite Guests
          </h1>
          <p className="text-gray-500">
            Please fill the guests details to proceed
          </p>
        </div>

        {/* Row 1: Number of people, Group name */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          <div>
            <label className="block text-gray-700 mb-2 font-medium">
              How many people in the group
              {groupErrors.numberOfGuests && (
                <span className="text-red-500 text-sm ml-2">
                  {groupErrors.numberOfGuests}
                </span>
              )}
            </label>
            <input
              type="number"
              min="1"
              max="20"
              value={numberOfGuests}
              onChange={(e) => setNumberOfGuests(parseInt(e.target.value) || 2)}
              required
              placeholder="Enter number of people"
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-gray-700 mb-2 font-medium">
              Building
              {groupErrors.building && (
                <span className="text-red-500 text-sm ml-2">
                  {groupErrors.building}
                </span>
              )}
            </label>
            {renderOtherableSelect({ field: "building", formKey: "building", options: BUILDINGS, placeholder: "Building", value: formData.building })}
          </div>

          <div>
  <label className="block text-gray-700 mb-2 font-medium">
    Meeting Location
    {groupErrors.meetingLocation && (
      <span className="text-red-500 text-sm ml-2">
        {groupErrors.meetingLocation}
      </span>
    )}
  </label>
  {renderOtherableSelect({ field: "meetingLocation", formKey: "meetingLocation", options: MEETING_LOCATIONS, placeholder: "Meeting Location", value: formData.meetingLocation })}
</div>
        </div>

        {/* Row 2: Building, Date and Time */}
        <div className="grid grid-cols-3 gap-6 mb-6">
          <div>
            <label className="block text-gray-700 mb-2 font-medium">
              Date of visit
              {groupErrors.dateOfVisit && (
                <span className="text-red-500 text-sm ml-2">
                  {groupErrors.dateOfVisit}
                </span>
              )}
            </label>
            <input
              type="date"
              required
              placeholder="Select date"
              value={formData.dateOfVisit}
              min={new Date().toISOString().split("T")[0]}
              onChange={(e) =>
                setFormData({ ...formData, dateOfVisit: e.target.value })
              }
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-2 font-medium">
              Start time
              {groupErrors.startTime && (
                <span className="text-red-500 text-sm ml-2">
                  {groupErrors.startTime}
                </span>
              )}
            </label>
            <input
              type="time"
              required
              value={formData.startTime}
              onChange={(e) =>
                setFormData({ ...formData, startTime: e.target.value })
              }
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-2 font-medium">
              End time
              {groupErrors.endTime && (
                <span className="text-red-500 text-sm ml-2">
                  {groupErrors.endTime}
                </span>
              )}
            </label>
            <input
              type="time"
              value={formData.endTime}
              onChange={(e) =>
                setFormData({ ...formData, endTime: e.target.value })
              }
              required
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Row 3: Purpose and Visitor Type */}
        <div className="grid grid-cols-2 mb-4 gap-4">
          <div>
            <label className="block text-gray-700 mb-2 font-medium">
              Purpose of visit
              {groupErrors.purposeOfVisit && (
                <span className="text-red-500 text-sm ml-2">
                  {groupErrors.purposeOfVisit}
                </span>
              )}
            </label>
            {renderOtherableSelect({ field: "purpose", formKey: "purposeOfVisit", options: PURPOSES, placeholder: "Purpose", value: formData.purposeOfVisit })}
          </div>

          <div>
  <label className="block text-gray-700 mb-4 font-medium">
    Type of Visitor
    {groupErrors.visitorType && (
      <span className="text-red-500 text-sm ml-2">
        {groupErrors.visitorType}
      </span>
    )}
  </label>
  {renderOtherableSelect({ field: "visitorType", formKey: "visitorType", options: VISITOR_TYPES, placeholder: "Visitor Type", value: formData.visitorType })}
</div>
        </div>

        {/* Document and Approval checkboxes */}
        <div className="mt-4 space-y-3">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="groupRequiresDocument"
                checked={formData.requiresDocument}
                onChange={(e) =>
                  setFormData(prev => ({ ...prev, requiresDocument: e.target.checked, documentType: e.target.checked ? prev.documentType : "" }))
                }
                className="mr-2 w-4 h-4 text-blue-800 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="groupRequiresDocument" className="text-gray-700 font-medium whitespace-nowrap">Document submission required?</label>
            </div>
            {formData.requiresDocument && (
              <div className="flex items-center gap-2 flex-wrap">
                <label className="text-gray-700 font-medium text-sm whitespace-nowrap">Document Type</label>
                {showOther.documentType ? (
                  <div className="relative">
                    <input type="text" value={formData.documentType} onChange={(e) => setFormData(prev => ({ ...prev, documentType: e.target.value }))} placeholder="Specify document type..." autoFocus className="p-2 border border-blue-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm pr-8 w-52" />
                    <button type="button" onClick={() => { setShowOther(prev => ({ ...prev, documentType: false })); setFormData(prev => ({ ...prev, documentType: "" })); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-xl leading-none">×</button>
                  </div>
                ) : (
                  <select value={formData.documentType} onChange={(e) => { if (e.target.value === "others") { setShowOther(prev => ({ ...prev, documentType: true })); setFormData(prev => ({ ...prev, documentType: "" })); } else { setFormData(prev => ({ ...prev, documentType: e.target.value })); } }} className="p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm">
                    <option value="">Select type</option>
                    <option value="passport">Passport</option>
                    <option value="permanent_ic">Permanent Identity Card</option>
                    <option value="others">Others, please specify</option>
                  </select>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="groupIsApprovalRequired"
              checked={formData.isApprovalRequired}
              onChange={(e) => setFormData(prev => ({...prev, isApprovalRequired: e.target.checked}))}
              className="mr-2 w-4 h-4 text-blue-800 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="groupIsApprovalRequired" className="text-gray-700 font-medium whitespace-nowrap">Approval Required?</label>
          </div>
        </div>
      </div>
    );
  }

  // Group Step 2: Individual guest details
  if (groupStep === 2) {
    return (
      <div className="max-w-7xl pl-12 w-full">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            Invite Guests
          </h1>
          <p className="text-gray-500">
            Please fill the guests details to proceed
          </p>
        </div>

        {/* Header Row */}
        <div className="grid grid-cols-3 gap-4 mb-4 text-sm font-medium text-gray-600">
          <div>
            Name
            {groupErrors[`guest0Name`] && (
              <span className="text-red-500 text-xs block">Required</span>
            )}
          </div>
          <div>
            Email
            {groupErrors[`guest0Email`] && (
              <span className="text-red-500 text-xs block">Required</span>
            )}
          </div>
          <div>
            Company
            {groupErrors[`guest0Company`] && (
              <span className="text-red-500 text-xs block">Required</span>
            )}
          </div>
        </div>

        {/* Guest details */}
        <div className="space-y-4">
          {guests.map((guest, index) => (
            <div key={index} className="grid grid-cols-3 gap-4 mb-4">
              <div className="relative">
                <input
                  type="text"
                  required
                  placeholder="Enter full name"
                  value={guest.name}
                  onChange={(e) => {
                    updateGuest(index, "name", e.target.value);
                    const suggestions = getFilteredContacts(
                      e.target.value,
                      "name"
                    );
                    setShowGroupSuggestions({
                      [`guest${index}`]:
                        suggestions.length > 0 ? suggestions : false,
                    });
                  }}
                  onFocus={(e) => {
                    const suggestions = getFilteredContacts(
                      e.target.value,
                      "name"
                    );
                    setShowGroupSuggestions({
                      [`guest${index}`]:
                        suggestions.length > 0 ? suggestions : false,
                    });
                  }}
                  onBlur={() => {
                    setTimeout(() => setShowGroupSuggestions({}), 150);
                  }}
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    groupErrors[`guest${index}Name`]
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />

                {/* Suggestions dropdown */}
                {showGroupSuggestions[`guest${index}`] && (
                  <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-32 overflow-y-auto">
                    {showGroupSuggestions[`guest${index}`].map(
                      (contact, contactIndex) => (
                        <div
                          key={contactIndex}
                          onClick={() =>
                            handleGroupContactSelect(contact, index)
                          }
                          className="p-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="text-xs font-medium text-gray-900">
                            {contact.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {contact.email}
                          </div>
                          <div className="text-xs text-gray-400">
                            {contact.company}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                )}

                {groupErrors[`guest${index}Name`] && (
                  <span className="text-red-500 text-xs">
                    {groupErrors[`guest${index}Name`]}
                  </span>
                )}
              </div>

              <div>
                <input
                  type="email"
                  required
                  placeholder="Enter email id"
                  value={guest.email}
                  onChange={(e) => updateGuest(index, "email", e.target.value)}
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    groupErrors[`guest${index}Email`]
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                {groupErrors[`guest${index}Email`] && (
                  <span className="text-red-500 text-xs">
                    {groupErrors[`guest${index}Email`]}
                  </span>
                )}
                {/* Show existing contact message for this guest */}
                {groupContactMessages[index] && (
                  <span className="text-blue-800 text-xs">
                    {groupContactMessages[index]}
                  </span>
                )}
              </div>
              <div>
                <input
                  type="text"
                  required
                  placeholder="Enter company"
                  value={guest.company}
                  onChange={(e) =>
                    updateGuest(index, "company", e.target.value)
                  }
                  className={`w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    groupErrors[`guest${index}Company`]
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />
                {groupErrors[`guest${index}Company`] && (
                  <span className="text-red-500 text-xs">
                    {groupErrors[`guest${index}Company`]}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center mt-8">
          <input
            type="checkbox"
            checked={saveGroupToAddressBook}
            onChange={(e) => {
              setSaveGroupToAddressBook(e.target.checked);
              if (!e.target.checked) {
                setGroupContactMessages({});
              }
            }}
            className="mr-3 w-4 h-4 text-blue-800 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
          />
          <label className="text-gray-700 font-medium">
            Save all guests as Frequent Visitors
          </label>
        </div>
      </div>
    );
  }

  // Group Step 3: Confirmation
  return (
    <div className="max-w-4xl mx-auto w-full">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-semibold text-gray-900 mb-2">
          Confirm Group Visit
        </h1>
        <p className="text-gray-500">
          Please confirm the guest details before sending the invite
        </p>
      </div>

      <div className="bg-gray-50 p-8 rounded-lg border border-gray-200">
        {/* Visitors Detail Table */}
        <div className="mb-6">
          <p className="font-semibold text-gray-900 mb-4">Visitors detail:</p>
          <div className="space-y-3">
            {guests.map((guest, index) => (
              <div
                key={index}
                className="grid grid-cols-3 gap-4 text-sm py-2 border-b border-gray-200"
              >
                <span className="font-medium">{guest.name}</span>
                <span>{guest.email}</span>
                <span>{guest.company}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Common Visitor Type */}
        <div className="mb-6">
          <p className="text-gray-700">
            <span className="font-semibold text-gray-900">Visitor Type:</span>{" "}
            {formData.visitorType}
          </p>
        </div>

       {/* Other Details */}
<div className="grid grid-cols-2 gap-6">
  <div className="space-y-3">
    <p className="text-gray-700">
      <span className="font-semibold text-gray-900">
        Date and Time:
      </span>{" "}
      {formData.dateOfVisit} at {formData.startTime}
    </p>
    <p className="text-gray-700">
      <span className="font-semibold text-gray-900">Building:</span>{" "}
      {formData.building}
    </p>
    <p className="text-gray-700">
      <span className="font-semibold text-gray-900">Meeting Location:</span>{" "}
      {formData.meetingLocation}
    </p>
    <p className="text-gray-700">
      <span className="font-semibold text-gray-900">
        Purpose of visit:
      </span>{" "}
      {formData.purposeOfVisit}
    </p>
  </div>
  <div className="space-y-3">
    <p className="text-gray-700">
      <span className="font-semibold text-gray-900">Visitor Type:</span>{" "}
      {formData.visitorType}
    </p>
    <p className="text-gray-700">
      <span className="font-semibold text-gray-900">Duration:</span>{" "}
      {calculateDuration(formData.startTime, formData.endTime)}
    </p>
    <p className="text-gray-700">
      <span className="font-semibold text-gray-900">Recurring:</span>{" "}
      {formData.recurringVisit ? "Yes" : "No"}
    </p>
    {formData.recurringVisit && (
      <>
        <p className="text-gray-700">
          <span className="font-semibold text-gray-900">Repeat:</span>{" "}
          {formData.repeatFrequency}
        </p>
        <p className="text-gray-700">
          <span className="font-semibold text-gray-900">Dates:</span>{" "}
          {formData.recurringDates}
        </p>
      </>
    )}
  </div>
</div>
      </div>
    </div>
  );
};

// Confirmation Component
const ConfirmationStep = ({ formData, selectedType }) => (
  <div className="max-w-2xl mx-auto w-full">
    <div className="text-center mb-8">
      <h1 className="text-2xl font-semibold text-gray-900 mb-2">
        Confirm Visit
      </h1>
      <p className="text-gray-500">
        Please confirm the guest details before sending the invite
      </p>
    </div>

    <div className="bg-gray-50 p-8 rounded-lg border border-gray-200">
      <div className="grid grid-cols-2 gap-10">
        <div className="space-y-3">
          <p className="text-gray-700">
            <span className="font-semibold text-gray-900">Name:</span>{" "}
            {formData.name}
          </p>
          <p className="text-gray-700">
            <span className="font-semibold text-gray-900">Email:</span>{" "}
            {formData.email}
          </p>
          <p className="text-gray-700">
            <span className="font-semibold text-gray-900">Company Name:</span>{" "}
            {formData.companyName}
          </p>
          <p className="text-gray-700">
            <span className="font-semibold text-gray-900">Visitor Type:</span>{" "}
            {formData.visitorType}
          </p>
          <p className="text-gray-700">
            <span className="font-semibold text-gray-900">Building:</span>{" "}
            {formData.building}
          </p>
          <p className="text-gray-700">
            <span className="font-semibold text-gray-900">Meeting Location:</span>{" "}
            {formData.meetingLocation}
          </p>
        </div>
        <div className="space-y-3">
          <p className="text-gray-700">
            <span className="font-semibold text-gray-900">Date and Time:</span>{" "}
            {formData.dateOfVisit} at {formData.startTime}
          </p>
          <p className="text-gray-700">
            <span className="font-semibold text-gray-900">
              Purpose of visit:
            </span>{" "}
            {formData.purposeOfVisit}
          </p>
          <p className="text-gray-700">
            <span className="font-semibold text-gray-900">Duration:</span>{" "}
            {calculateDuration(formData.startTime, formData.endTime)}
          </p>
          {formData.isDriving && (
            <>
              <p className="text-gray-700">
                <span className="font-semibold text-gray-900">Drive-in:</span> Yes
              </p>
              <p className="text-gray-700">
                <span className="font-semibold text-gray-900">License Plate:</span>{" "}
                {formData.licensePlate || "-"}
              </p>
            </>
          )}
          {formData.requiresDocument && (
            <p className="text-gray-700">
              <span className="font-semibold text-gray-900">Document Required:</span>{" "}
              {formData.documentType === "passport"
                ? "Passport"
                : formData.documentType === "permanent_ic"
                ? "Permanent Identity Card"
                : formData.documentType || "Document"}
            </p>
          )}
        </div>
      </div>
    </div>
  </div>
);

// Calculate duration between start and end time
const calculateDuration = (startTime, endTime) => {
  if (!startTime || !endTime) return "2 hours"; // default fallback

  const start = new Date(`2024-01-01 ${startTime}`);
  const end = new Date(`2024-01-01 ${endTime}`);
  const diffMs = end - start;
  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours === 1) return "1 hour";
  return `${diffHours.toFixed(1)} hours`;
};

// API function to send invite
const sendInviteAPI = async (inviteData) => {
  try {
    console.log("Sending invite data:", inviteData);

    const response = await fetch("https://vms-backend-dot-vms-demo-495410.el.r.appspot.com/api/visits", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(inviteData),
    });

    const responseText = await response.text();
    console.log("Response:", responseText);

    if (!response.ok) {
      throw new Error(
        `HTTP error! status: ${response.status}, message: ${responseText}`
      );
    }

    return JSON.parse(responseText);
  } catch (error) {
    console.error("Error sending invite:", error);
    throw error;
  }
};

const Invite = () => {
  const [currentTime, setCurrentTime] = useState("");
  const [selectedType, setSelectedType] = useState("Individual");
  const navigate = useNavigate();
  const [groupStep, setGroupStep] = useState(1);
  const [numberOfGuests, setNumberOfGuests] = useState(2);
  const [errors, setErrors] = useState({});
  const [groupErrors, setGroupErrors] = useState({});
  const [saveGroupToAddressBook, setSaveGroupToAddressBook] = useState(false);
  const [groupContactMessages, setGroupContactMessages] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [saveToAddressBook, setSaveToAddressBook] = useState(false);
  const [existingContactMessage, setExistingContactMessage] = useState("");
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState({});
  const [showGroupSuggestions, setShowGroupSuggestions] = useState({});
  const [guests, setGuests] = useState([
    { name: "", email: "", company: "" },
    { name: "", email: "", company: "" },
  ]);

  // Buildings, purposes, meetingLocations, visitorTypes now use hardcoded constants above

  const currentUser = JSON.parse(localStorage.getItem("user"));
  const hostEmail = currentUser?.email;
  const hostUsername = currentUser?.username;

  const [currentStep, setCurrentStep] = useState(1); // Track current step (1, 2, 3)
  const [formData, setFormData] = useState({
    // Individual form data
    name: "",
    email: "",
    companyName: "",
    building: "",
    buildingId: "",
    meetingLocation: "",
    meetingLocationId: "", 
    dateOfVisit: "",
    visitorType: "",
    startTime: "",
    endTime: "",
    recurringVisit: false,
    repeatFrequency: "Daily",
    recurringDates: "",
    purposeOfVisit: "",
    isDriving: false,
    licensePlate: "",
    requiresDocument: false,
    documentType: "",
    isApprovalRequired: false,

    // Group form data
    groupEmail: "",
  });

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("access_token");

    navigate("/");
  };

  const fetchContacts = async () => {
    const userEmail = currentUser?.email;
    
    if (!userEmail) {
      console.log("Waiting for user email...");
      return;
    }
    
    try {
      const url = `https://vms-backend-dot-vms-demo-495410.el.r.appspot.com/api/visits/contacts?hostEmail=${encodeURIComponent(userEmail)}`;
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setContacts(data);
      } else {
        console.error("Failed to fetch contacts");
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  useEffect(() => {
    if (currentUser?.email) {
      fetchContacts();
    }
  }, [currentUser?.email]);

 


  const getFilteredContacts = (query, field = "name") => {
    if (!query || query.length < 2) return [];

    return contacts
      .filter((contact) => {
        if (field === "name") {
          return contact.name?.toLowerCase().includes(query.toLowerCase());
        }
        return false;
      })
      .slice(0, 5); // Limit to 5 suggestions
  };

  const handleContactSelect = (contact) => {
    setFormData({
      ...formData,
      name: contact.name,
      email: contact.email || "",
      companyName: contact.company || "",
    });
    setShowSuggestions({});
  };

  const handleGroupContactSelect = (contact, guestIndex) => {
    const updatedGuests = [...guests];
    updatedGuests[guestIndex] = {
      ...updatedGuests[guestIndex],
      name: contact.name,
      email: contact.email || "",
      company: contact.company || "",
    };
    setGuests(updatedGuests);
    setShowGroupSuggestions({});
  };

  const checkGroupContactsExist = async (guests) => {
    if (!currentUser?.email) return {};
    
    try {
      const response = await fetch(
        `https://vms-backend-dot-vms-demo-495410.el.r.appspot.com/api/visits/contacts?hostEmail=${encodeURIComponent(currentUser.email)}`
      );
      if (response.ok) {
        const contacts = await response.json();
        const messages = {};
  
        guests.forEach((guest, index) => {
          if (guest.email) {
            const exists = contacts.some(
              (contact) =>
                contact.email.toLowerCase() === guest.email.toLowerCase()
            );
            if (exists) {
              messages[index] =
                "This contact already exists in the address book.";
            }
          }
        });
  
        setGroupContactMessages(messages);
        return messages;
      }
    } catch (error) {
      console.error("Error checking contacts:", error);
    }
    return {};
  };

  useEffect(() => {
    const checkGroupContacts = async () => {
      if (saveGroupToAddressBook && guests.length > 0) {
        await checkGroupContactsExist(guests);
      } else {
        setGroupContactMessages({});
      }
    };

    const timeoutId = setTimeout(checkGroupContacts, 500); // Debounce
    return () => clearTimeout(timeoutId);
  }, [guests, saveGroupToAddressBook]);

  const saveGroupContactsToAddressBook = async (guests) => {
    if (!currentUser?.username || !currentUser?.email) {
      console.error("User information not available");
      return [];
    }
  
    const existingMessages = await checkGroupContactsExist(guests);
  
    const savePromises = guests.map(async (guest, index) => {
      // Only save if contact doesn't already exist
      if (!existingMessages[index]) {
        try {
          const response = await fetch(
            "https://vms-backend-dot-vms-demo-495410.el.r.appspot.com/api/visits/contacts",
            {
              method: "POST",
              headers: {

                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                name: guest.name,
                email: guest.email,
                company: guest.company,
                shared: false,
                username: currentUser.username,
                hostEmployee: currentUser.email,
              }),
            }
          );
  
          if (!response.ok) {
            throw new Error("Failed to save contact");
          }
          return true;
        } catch (error) {
          console.error(`Error saving contact ${index}:`, error);
          return false;
        }
      }
      return true; // Skip existing contacts
    });
  
    return Promise.all(savePromises);
  };

  useEffect(() => {
    const checkExistingContact = async () => {
      if (saveToAddressBook && formData.email) {
        const exists = await checkContactExists(formData.email);
        if (exists) {
          setExistingContactMessage(
            "This contact already exists in the address book."
          );
        } else {
          setExistingContactMessage("");
        }
      }
    };

    const timeoutId = setTimeout(checkExistingContact, 500); // Debounce
    return () => clearTimeout(timeoutId);
  }, [formData.email, saveToAddressBook]);

  // Update time every second
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const timeString = now.toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
      });
      setCurrentTime(timeString);
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const token = await getAccessToken();
        const response = await fetch(`${config.audience}users`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          const storedUser = JSON.parse(localStorage.getItem("user"));

          const auth0User = data.find((u) => u.email === storedUser?.email);

          if (auth0User) {
            const userData = {
              username:
                auth0User.user_metadata?.username ||
                auth0User.email.split("@")[0],
              email: auth0User.email,
              sub: auth0User.sub,
              role: storedUser?.role || "Employee", // Keep existing role
            };

            localStorage.setItem("user", JSON.stringify(userData));
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchCurrentUser();
  }, []);

  const checkContactExists = async (email) => {
    if (!currentUser?.email) return false;
    
    try {
      const response = await fetch(
        `https://vms-backend-dot-vms-demo-495410.el.r.appspot.com/api/visits/contacts?hostEmail=${encodeURIComponent(currentUser.email)}`
      );
      if (response.ok) {
        const contacts = await response.json();
        return contacts.some(
          (contact) => contact.email.toLowerCase() === email.toLowerCase()
        );
      }
    } catch (error) {
      console.error("Error checking contacts:", error);
    }
    return false;
  };

  const saveContactToAddressBook = async (contactData) => {
    if (!currentUser?.username || !currentUser?.email) {
      console.error("User information not available");
      return false;
    }
  
    try {
      const response = await fetch(
        "https://vms-backend-dot-vms-demo-495410.el.r.appspot.com/api/visits/contacts",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            name: contactData.name,
            email: contactData.email,
            company: contactData.company,
            shared: false,
            username: currentUser.username,
            hostEmployee: currentUser.email,
          }),
        }
      );
  
      if (!response.ok) {
        throw new Error("Failed to save contact");
      }
      return true;
    } catch (error) {
      console.error("Error saving contact:", error);
      return false;
    }
  };

  const validateIndividualForm = () => {
    const newErrors = {};
    const required = {
      name: "required",
      email: " required",
      companyName: "required",
      building: "required",
      dateOfVisit: "required",
      startTime: "required",
      endTime: "required",
      purposeOfVisit: "required",
    };

    Object.keys(required).forEach((field) => {
      if (!formData[field] || formData[field].trim() === "") {
        newErrors[field] = required[field];
      }
    });

    if (!formData.visitorType) {
      newErrors.visitorType = "required";
    }

    if (formData.isDriving && !formData.licensePlate?.trim()) {
      newErrors.licensePlate = "required";
    }

    if (formData.requiresDocument && !formData.documentType) {
      newErrors.documentType = "required";
    }

    // Date validation - cannot be before current date
    if (formData.dateOfVisit) {
      const today = new Date();
      const selectedDate = new Date(formData.dateOfVisit);
      today.setHours(0, 0, 0, 0); // Reset time to start of day

      if (selectedDate < today) {
        newErrors.dateOfVisit = "visit cannot be before today";
      }
    }

    // Time validation - end time cannot be before start time
    if (formData.startTime && formData.endTime) {
      const startTime = new Date(`2000-01-01 ${formData.startTime}`);
      const endTime = new Date(`2000-01-01 ${formData.endTime}`);

      if (endTime <= startTime) {
        newErrors.endTime = "End time must be after start time";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateGroupStep1 = () => {
    const newErrors = {};
    const required = {
      building: "required",
      dateOfVisit: "required",
      startTime: "required",
      endTime: "required",
      purposeOfVisit: "required",
    };

    Object.keys(required).forEach((field) => {
      if (!formData[field] || formData[field].trim() === "") {
        newErrors[field] = required[field];
      }
    });

    if (!formData.visitorType) {
      newErrors.visitorType = "Visitor type is required";
    }

    if (numberOfGuests < 1) {
      newErrors.numberOfGuests = "Number of guests is required";
    }

    // Date validation - cannot be before current date
    if (formData.dateOfVisit) {
      const today = new Date();
      const selectedDate = new Date(formData.dateOfVisit);
      today.setHours(0, 0, 0, 0); // Reset time to start of day

      if (selectedDate < today) {
        newErrors.dateOfVisit = "Date of visit cannot be before today";
      }
    }

    // Time validation - end time cannot be before start time
    if (formData.startTime && formData.endTime) {
      const startTime = new Date(`2000-01-01 ${formData.startTime}`);
      const endTime = new Date(`2000-01-01 ${formData.endTime}`);

      if (endTime <= startTime) {
        newErrors.endTime = "End time must be after start time";
      }
    }

    setGroupErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateGroupStep2 = () => {
    const newErrors = {};

    guests.forEach((guest, index) => {
      if (!guest.name || guest.name.trim() === "") {
        newErrors[`guest${index}Name`] = "Name is required";
      }
      if (!guest.email || guest.email.trim() === "") {
        newErrors[`guest${index}Email`] = "Email is required";
      }
      if (!guest.company || guest.company.trim() === "") {
        newErrors[`guest${index}Company`] = "Company is required";
      }
    });

    setGroupErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      setCurrentStep(2);
    } else if (currentStep === 2) {
      if (selectedType === "Individual") {
        if (!validateIndividualForm()) {
          return;
        }
        setCurrentStep(3);
      } else if (selectedType === "Group") {
        if (groupStep === 1) {
          if (!validateGroupStep1()) {
            return;
          }
          setGroupStep(2);
        } else if (groupStep === 2) {
          if (!validateGroupStep2()) {
            return;
          }
          setGroupStep(3);
        } else if (groupStep === 3) {
          try {
            setIsLoading(true);

            // Save to address book if checkbox is checked
            if (saveGroupToAddressBook) {
              await saveGroupContactsToAddressBook(guests);
            }

            // Create proper date objects
            const startDateTime = new Date(
              `${formData.dateOfVisit} ${formData.startTime}`
            );
            const endDateTime = new Date(
              `${formData.dateOfVisit} ${formData.endTime}`
            );

            const invitePromises = guests.map((guest) => {
              console.log('FormData before sending:', {
                buildingId: formData.buildingId,
                meetingLocationId: formData.meetingLocationId,
                building: formData.building,
                meetingLocation: formData.meetingLocation
              });
              const inviteData = {
                visitor: {
                  Name: guest.name,
                  email: guest.email,
                  company: guest.company,
                },
                hostEmail: hostEmail,
                hostName: currentUser?.name || hostUsername,
                building: formData.building,
                buildingName: formData.building,
                typeOfVisitor: formData.visitorType,
                purpose: formData.purposeOfVisit || "Meeting",
                meetingLocation: formData.meetingLocation,
                startTime: startDateTime.toISOString(),
                endTime: endDateTime.toISOString(),
                isDriving: formData.isDriving,
                licensePlate: formData.isDriving ? formData.licensePlate : null,
                requiresDocument: formData.requiresDocument,
                documentType: formData.requiresDocument ? formData.documentType : null,
                requiresDocumentApproval: formData.requiresDocument,
                isApprovalRequired: formData.isApprovalRequired,
              };
              return sendInviteAPI(inviteData);
            });

            await Promise.all(invitePromises);
            navigate("/log", {
              state: {
                showSuccessMessage: true,
                newRecordCount: guests.length,
              },
            });
          } catch (error) {
            console.error("Full error:", error);
            alert("Error sending group invites: " + error.message);
          } finally {
            setIsLoading(false);
          }
          return;
        }
      }
    } else if (currentStep === 3) {
      try {
        setIsLoading(true);

        // Save to address book if checkbox is checked and contact doesn't exist
if (saveToAddressBook && !existingContactMessage) {
  const saved = await saveContactToAddressBook({
    name: formData.name,
    email: formData.email,
    company: formData.companyName,
  });
  
  if (!saved) {
    console.warn("Failed to save contact as frequent visitor");
    // Optionally show alert: alert("Visit created but failed to save contact");
  }
}

        // Create proper date objects
        const startDateTime = new Date(
          `${formData.dateOfVisit} ${formData.startTime}`
        );
        const endDateTime = new Date(
          `${formData.dateOfVisit} ${formData.endTime}`
        );

        const inviteData = {
          visitor: {
            Name: formData.name,
            email: formData.email,
            company: formData.companyName,
          },
          hostEmail: hostEmail,
          hostName: currentUser?.name || hostUsername,
          building: formData.building,
          buildingName: formData.building,
          typeOfVisitor: formData.visitorType,
          purpose: formData.purposeOfVisit || "Meeting",
          meetingLocation: formData.meetingLocation,
          startTime: startDateTime.toISOString(),
          endTime: endDateTime.toISOString(),
          isDriving: formData.isDriving,
          licensePlate: formData.isDriving ? formData.licensePlate : null,
          requiresDocument: formData.requiresDocument,
          documentType: formData.requiresDocument ? formData.documentType : null,
          requiresDocumentApproval: formData.requiresDocument,
          isApprovalRequired: formData.isApprovalRequired,
        };

        console.log("Individual invite data:", inviteData);
        await sendInviteAPI(inviteData);
        navigate("/log", {
          state: {
            showSuccessMessage: true,
            newRecordCount: 1,
          },
        });
      } catch (error) {
        console.error("Full error:", error);
        alert("Error sending individual invite: " + error.message);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleBack = () => {
    if (currentStep === 3) {
      if (selectedType === "Group") {
        setCurrentStep(2);
        setGroupStep(3); // Go back to group confirmation
      } else {
        setCurrentStep(2); // Individual goes back to form
      }
    } else if (currentStep === 2) {
      if (selectedType === "Group") {
        if (groupStep === 3) {
          setGroupStep(2); // Go back to group details
        } else if (groupStep === 2) {
          setGroupStep(1); // Go back to group basic info
        } else if (groupStep === 1) {
          setCurrentStep(1); // Go back to type selection
        }
      } else {
        setCurrentStep(1); // Individual goes back to type selection
      }
    }
  };

  return (
    <div className="min-h-screen">
      <Sidebar onLogout={handleLogout} />

      {/* Time Display */}
      <div className="absolute top-4 right-6 text-gray-600 font-medium text-xl">
        {currentTime}
      </div>

      {/* Main Content */}
      <div className="mx-auto min-h-screen flex flex-col">
        {/* Content Area */}
        <div className="flex-1 flex flex-col items-center justify-center px-8 py-16">
          {currentStep === 1 && (
            <>
              {/* Step 1: Select invitation type */}
              <div className="text-center mb-16">
                <h1 className="text-3xl font-semibold text-gray-900 mb-3">
                  Select your invitation type
                </h1>
                <p className="text-gray-500">Please select to proceed</p>
              </div>

              {/* Cards */}
              <div className="flex gap-6 mb-20">
                {/* Individual Card */}
                <div
                  className={`w-72 h-40 border-2 rounded-2xl p-6 cursor-pointer transition-all ${
                    selectedType === "Individual"
                      ? "border-blue-800 bg-white"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                  onClick={() => setSelectedType("Individual")}
                >
                  <div className="flex flex-col items-center justify-center h-full">
                    <User
                      className="w-12 h-12 text-gray-800 mb-3"
                      strokeWidth={1.5}
                    />
                    <h3 className="text-lg font-medium text-gray-900">
                      Individual
                    </h3>
                  </div>
                </div>

                {/* Group Card */}
                <div
                  className={`w-72 h-40 border-2 rounded-2xl p-6 cursor-pointer transition-all ${
                    selectedType === "Group"
                      ? "border-blue-800 bg-white"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                  onClick={() => setSelectedType("Group")}
                >
                  <div className="flex flex-col items-center justify-center h-full">
                    <Users
                      className="w-12 h-12 text-gray-800 mb-3"
                      strokeWidth={1.5}
                    />
                    <h3 className="text-lg font-medium text-gray-900">Group</h3>
                  </div>
                </div>
              </div>
            </>
          )}

          {currentStep === 2 && selectedType === "Individual" && (
            <IndividualForm
              formData={formData}
              setFormData={setFormData}
              saveToAddressBook={saveToAddressBook}
              setSaveToAddressBook={setSaveToAddressBook}
              existingContactMessage={existingContactMessage}
              setExistingContactMessage={setExistingContactMessage}
              errors={errors}
              contacts={contacts}
              showSuggestions={showSuggestions}
              setShowSuggestions={setShowSuggestions}
              getFilteredContacts={getFilteredContacts}
              handleContactSelect={handleContactSelect}
            />
          )}

          {currentStep === 2 && selectedType === "Group" && (
            <GroupForm
              formData={formData}
              setFormData={setFormData}
              groupStep={groupStep}
              numberOfGuests={numberOfGuests}
              setNumberOfGuests={setNumberOfGuests}
              guests={guests}
              setGuests={setGuests}
              groupErrors={groupErrors}
              saveGroupToAddressBook={saveGroupToAddressBook}
              setSaveGroupToAddressBook={setSaveGroupToAddressBook}
              groupContactMessages={groupContactMessages}
              setGroupContactMessages={setGroupContactMessages}
              contacts={contacts}
              showGroupSuggestions={showGroupSuggestions}
              setShowGroupSuggestions={setShowGroupSuggestions}
              getFilteredContacts={getFilteredContacts}
              handleGroupContactSelect={handleGroupContactSelect}
            />
          )}

          {currentStep === 3 && selectedType === "Individual" && (
            <ConfirmationStep formData={formData} selectedType={selectedType} />
          )}
        </div>

        {/* Bottom Section with Line and Next Button */}
        <div className="px-8 pb-8">
          <div className="w-[92%] h-px ml-24 bg-gray-300 mb-6"></div>

          <div className="flex justify-between">
            {currentStep > 1 && (
              <button
                onClick={handleBack}
                className="px-8 py-3 ml-24 border border-gray-300 bg-white text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Back
              </button>
            )}

            <button
              onClick={handleNext}
              disabled={isLoading}
              className="px-8 py-3 bg-blue-800 text-white rounded-lg font-medium hover:bg-blue-800 transition-colors ml-auto disabled:opacity-50"
            >
              {isLoading
                ? "Sending..."
                : currentStep === 3 ||
                  (currentStep === 2 &&
                    selectedType === "Group" &&
                    groupStep === 3)
                ? "Send Invite"
                : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Invite;
